import { apiClient, localAgentBaseUrl } from './client'

export interface LabelsScopeRequest {
  km_only?: boolean
  ka_only?: boolean
  force?: boolean
  copies?: number
}

export interface MarkTarget {
  target_type: string
  sequence_number: number
}

export interface LabelsResponse {
  km?: Array<{ sequence_number: number; url: string }>
  ka?: { url: string }
  ka_list?: Array<{ url: string }>
  mark_targets?: MarkTarget[]
}

export interface RecordPrintRequest {
  mark_targets: MarkTarget[]
  force?: boolean
}

export function flattenLabelUrls(item: LabelsResponse): string[] {
  const urls: string[] = []
  for (const km of item.km ?? []) urls.push(km.url)
  if (item.ka?.url) urls.push(item.ka.url)
  for (const ka of item.ka_list ?? []) urls.push(ka.url)
  return urls
}

export function labelsToPreview(item: LabelsResponse): Array<{ url: string; caption?: string }> {
  const out: Array<{ url: string; caption?: string }> = []
  for (const km of item.km ?? []) {
    out.push({ url: km.url, caption: `КМ ${km.sequence_number}` })
  }
  if (item.ka?.url) out.push({ url: item.ka.url, caption: 'КА' })
  for (const ka of item.ka_list ?? []) out.push({ url: ka.url, caption: 'КА коробки' })
  return out
}

export interface PrintRequest {
  agent_id: string
  force?: boolean
  copies?: number
}

export interface KMPrintItem {
  box_num: number
  sequence_number: number
}

export interface KMPrintRequest extends PrintRequest {
  items?: KMPrintItem[]
  sequence_numbers?: number[]
}

export interface RenderKmResult {
  rendered_count: number
  boxes_count: number
  output_dir: string
}

export interface RenderKmSession {
  task_uuid: string
  status: 'running' | 'completed' | 'failed'
  started_at: string
  finished_at?: string
  duration_ms?: number
  rendered_count?: number
  boxes_count?: number
  output_dir?: string
  error?: string
}

export interface LabelPrepareSession {
  session_id: string
  task_uuid: string
  status: 'running' | 'completed' | 'failed'
  total: number
  rendered_count: number
  percent: number
  error?: string
  labels?: LabelsResponse
}

export type LabelPrepareProgress = { percent: number; done: number; total: number }

export interface PrintJob {
  id: string
  task_uuid: string
  target_type: string
  sequence_number?: number
  agent_id?: string
  status: 'queued' | 'processing' | 'completed' | 'failed' | 'cancelled'
  payload?: {
    labels: Array<{ url: string; printer: string; copies: number; filename: string }>
    mark_targets: Array<{ target_type: string; sequence_number: number }>
  }
  error_message?: string
  created_at: string
}

export interface PrintAgentInfo {
  agent_id: string
  agent_url: string
  printer_km: string
  printer_ka: string
  hostname: string
  online: boolean
  last_seen_at: string
}

export interface LocalAgentStatus {
  agent_id: string
  server_url: string
  printer_km: string
  printer_ka: string
  online: boolean
  task_uuid?: string
  box_num?: number
  worker_name?: string
  scan_port?: string
  scan_baud?: number
  has_token?: boolean
}

export interface LocalAgentConfig {
  server_url: string
  printer_km?: string
  printer_ka?: string
  agent_id?: string
  agent_token?: string
  task_uuid?: string
  box_num?: number
  worker_name?: string
  scan_port?: string
  scan_baud?: number
}

const LOCAL_AGENT_URL = localAgentBaseUrl()

async function localFetch<T>(path: string, init?: RequestInit): Promise<T | null> {
  try {
    const res = await fetch(`${LOCAL_AGENT_URL}${path}`, {
      ...init,
      signal: AbortSignal.timeout(2000),
    })
    if (!res.ok) return null
    return (await res.json()) as T
  } catch {
    return null
  }
}

export const printApi = {
  /** Box aggregation label (KA) only. */
  printBox: (taskUuid: string, boxNum: number, body: PrintRequest) =>
    apiClient.post<{ item: PrintJob }>(`/api/print/tasks/${taskUuid}/boxes/${boxNum}/ka`, body),

  /** All KM in box plus box KA. */
  printBoxBundle: (taskUuid: string, boxNum: number, body: PrintRequest) =>
    apiClient.post<{ item: PrintJob }>(`/api/print/tasks/${taskUuid}/boxes/${boxNum}`, body),

  /** @deprecated use printBox — same endpoint */
  printBoxKa: (taskUuid: string, boxNum: number, body: PrintRequest) =>
    apiClient.post<{ item: PrintJob }>(`/api/print/tasks/${taskUuid}/boxes/${boxNum}/ka`, body),

  printPallet: (taskUuid: string, palletNum: number, body: PrintRequest) =>
    apiClient.post<{ item: PrintJob }>(`/api/print/tasks/${taskUuid}/pallets/${palletNum}`, body),

  printPalletBoxes: (taskUuid: string, palletNum: number, body: PrintRequest) =>
    apiClient.post<{ item: PrintJob }>(`/api/print/tasks/${taskUuid}/pallets/${palletNum}/boxes`, body),

  printKm: (taskUuid: string, body: KMPrintRequest) =>
    apiClient.post<{ item: PrintJob }>(`/api/print/tasks/${taskUuid}/km`, body),

  renderAllKm: (taskUuid: string) =>
    apiClient.post<{ item: RenderKmSession }>(`/api/print/tasks/${taskUuid}/km/render`, {}),

  getRenderKmStatus: (taskUuid: string) =>
    apiClient.get<{ item: RenderKmSession }>(`/api/print/tasks/${taskUuid}/km/render/status`),

  listActiveKmRenders: () => apiClient.get<RenderKmSession[]>('/api/print/km/render/active'),

  printKmAll: (taskUuid: string, body: PrintRequest) =>
    apiClient.post<{ item: PrintJob }>(`/api/print/tasks/${taskUuid}/km/all`, body),

  listAgents: () => apiClient.get<PrintAgentInfo[]>('/api/print/agents'),

  listJobs: (params?: { limit?: number }) => apiClient.get<PrintJob[]>('/api/print/jobs', { params }),

  getJob: (id: string) => apiClient.get<{ item: PrintJob }>(`/api/print/jobs/${id}`),

  getLocalAgentStatus: async (): Promise<LocalAgentStatus | null> => {
    return localFetch<LocalAgentStatus>('/status')
  },

  getLocalAgentConfig: () => localFetch<LocalAgentConfig>('/config'),

  putLocalAgentConfig: (cfg: Partial<LocalAgentConfig>) =>
    localFetch<{ ok: boolean }>('/config', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(cfg),
    }),

  listSerialPorts: () => localFetch<{ ports: string[] }>('/ports'),

  localAgentSettingsUrl: () => `${LOCAL_AGENT_URL.replace(/\/$/, '')}/settings`,

  boxLabels: (taskUuid: string, boxNum: number, body?: LabelsScopeRequest) =>
    apiClient.post<{ item: LabelsResponse }>(`/api/print/tasks/${taskUuid}/boxes/${boxNum}/labels`, body ?? {}),

  palletLabels: (taskUuid: string, palletNum: number, body?: LabelsScopeRequest) =>
    apiClient.post<{ item: LabelsResponse }>(`/api/print/tasks/${taskUuid}/pallets/${palletNum}/labels`, body ?? {}),

  palletBoxesLabels: (taskUuid: string, palletNum: number, body?: LabelsScopeRequest) =>
    apiClient.post<{ item: LabelsResponse }>(
      `/api/print/tasks/${taskUuid}/pallets/${palletNum}/boxes/labels`,
      body ?? {},
    ),

  kmLabels: (taskUuid: string, body: Pick<KMPrintRequest, 'sequence_numbers' | 'items' | 'force'>) =>
    apiClient.post<{ item: LabelsResponse }>(`/api/print/tasks/${taskUuid}/km/labels`, body),

  kmLabelsPrepare: (taskUuid: string, body: Pick<KMPrintRequest, 'sequence_numbers' | 'items' | 'force'>) =>
    apiClient.post<{ item: LabelPrepareSession }>(`/api/print/tasks/${taskUuid}/km/labels/prepare`, body),

  kmAllLabels: (taskUuid: string, body?: LabelsScopeRequest) =>
    apiClient.post<{ item: LabelsResponse }>(`/api/print/tasks/${taskUuid}/km/all/labels`, body ?? {}),

  kmAllLabelsPrepare: (taskUuid: string, body?: LabelsScopeRequest) =>
    apiClient.post<{ item: LabelPrepareSession }>(`/api/print/tasks/${taskUuid}/km/all/labels/prepare`, body ?? {}),

  getLabelPrepare: (sessionId: string) =>
    apiClient.get<{ item: LabelPrepareSession }>(`/api/print/labels/prepare/${sessionId}`),

  boxLabelsPrepare: (taskUuid: string, boxNum: number, body?: LabelsScopeRequest) =>
    apiClient.post<{ item: LabelPrepareSession }>(
      `/api/print/tasks/${taskUuid}/boxes/${boxNum}/labels/prepare`,
      body ?? {},
    ),

  palletLabelsPrepare: (taskUuid: string, palletNum: number, body?: LabelsScopeRequest) =>
    apiClient.post<{ item: LabelPrepareSession }>(
      `/api/print/tasks/${taskUuid}/pallets/${palletNum}/labels/prepare`,
      body ?? {},
    ),

  palletBoxesLabelsPrepare: (taskUuid: string, palletNum: number, body?: LabelsScopeRequest) =>
    apiClient.post<{ item: LabelPrepareSession }>(
      `/api/print/tasks/${taskUuid}/pallets/${palletNum}/boxes/labels/prepare`,
      body ?? {},
    ),

  recordPrint: (taskUuid: string, body: RecordPrintRequest) =>
    apiClient.post<{ item: { recorded: number } }>(`/api/print/tasks/${taskUuid}/record`, body),
}

export async function resolveAgentId(opts?: { localOnly?: boolean }): Promise<string> {
  const local = await printApi.getLocalAgentStatus()
  if (local?.agent_id) return local.agent_id
  if (opts?.localOnly) {
    throw new Error('Локальный агент не запущен на этом ПК. Запустите print-agent.')
  }
  const res = await printApi.listAgents()
  const agents = res.data
  const online = agents.find((a: PrintAgentInfo) => a.online)
  if (online) return online.agent_id
  throw new Error('Локальный агент не подключён. Запустите print-agent на этом ПК.')
}
